import { useState, useEffect, useRef, useCallback } from "react";
import useRingTone from "./useRingTone";
import { IceServer } from "@/app/lib/features/callStart/callStart.types";

export interface SipConfig {
  wssServer: string;
  uri: string;
  username: string;
  password: string;
  target: string;
  iceServers: IceServer[];
  displayName?: string;
}

export type CallStatus =
  | "idle"
  | "connecting"
  | "registered"
  | "calling"
  | "active"
  | "ended"
  | "error";

interface UseLiveSupportReturn {
  isOpen: boolean;
  isConnected: boolean;
  callStatus: CallStatus;
  statusMessage: string;
  open: () => void;
  close: () => void;
  makeCall: () => Promise<void>;
  hangup: () => Promise<void>;
}

const useLiveSupport = (sipConfig: SipConfig): UseLiveSupportReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [callStatus, setCallStatus] = useState<CallStatus>("idle");
  const [statusMessage, setStatusMessage] = useState("Bağlanılıyor...");

  const sipConfigRef = useRef<SipConfig>(sipConfig);
  useEffect(() => {
    sipConfigRef.current = sipConfig;
  });

  const userAgentRef = useRef<unknown>(null);
  const sessionRef = useRef<unknown>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const sipLoadedRef = useRef(false);
  const initCalledRef = useRef(false);
  const endedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const CONNECT_TIMEOUT_MS = 10000;
  const UNAVAILABLE_MESSAGE =
    "Canlı destek şu anda kullanılamıyor. Lütfen daha sonra tekrar deneyin.";

  const clearConnectTimeout = useCallback(() => {
    if (connectTimeoutRef.current) {
      clearTimeout(connectTimeoutRef.current);
      connectTimeoutRef.current = null;
    }
  }, []);

  const ringTone = useRingTone();

  const isConnected =
    callStatus === "registered" ||
    callStatus === "calling" ||
    callStatus === "active";

  const updateStatus = useCallback(
    (msg: string, status: CallStatus) => {
      if (status !== "connecting") clearConnectTimeout();
      setStatusMessage(msg);
      setCallStatus(status);
    },
    [clearConnectTimeout],
  );

  const attachRemoteAudio = useCallback((session: unknown) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sdh = (session as any)?.sessionDescriptionHandler;
    if (!sdh?.peerConnection) return;

    sdh.peerConnection.ontrack = (e: RTCTrackEvent) => {
      if (e.streams?.[0] && remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = e.streams[0];
      }
    };
  }, []);

  const cleanup = useCallback(() => {
    ringTone.stop();

    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }
    sessionRef.current = null;
    updateStatus("Arama sona erdi", "ended");

    if (endedTimerRef.current) clearTimeout(endedTimerRef.current);
    endedTimerRef.current = setTimeout(() => {
      updateStatus("Hazır", "registered");
    }, 3000);
  }, [updateStatus, ringTone]);

  const initSIPRef = useRef(async () => {});
  useEffect(() => {
    initSIPRef.current = async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const SIP = (window as any).SIP;
    if (!SIP || initCalledRef.current) return;
    initCalledRef.current = true;

    try {
      updateStatus("Bağlanılıyor...", "connecting");

      clearConnectTimeout();
      connectTimeoutRef.current = setTimeout(() => {
        updateStatus(UNAVAILABLE_MESSAGE, "error");
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (userAgentRef.current as any)?.stop?.();
        } catch {
          // ignore
        }
      }, CONNECT_TIMEOUT_MS);

      if (!remoteAudioRef.current) {
        remoteAudioRef.current = new Audio();
        remoteAudioRef.current.autoplay = true;
      }

      const cfg = sipConfigRef.current;
      const options = {
        uri: SIP.UserAgent.makeURI(cfg.uri),
        transportOptions: { wsServers: [cfg.wssServer], reconnectionAttempts: 0 },
        authorizationUsername: cfg.username,
        authorizationPassword: cfg.password,
        displayName: cfg.displayName ?? "Müşteri",
        sessionDescriptionHandlerFactoryOptions: {
          peerConnectionConfiguration: {
            iceServers: cfg.iceServers,
            iceTransportPolicy: "all",
          },
        },
      };

      const ua = new SIP.UserAgent(options);
      userAgentRef.current = ua;

      ua.delegate = {
        onConnect: () => {
          const registerer = new SIP.Registerer(ua);
          registerer
            .register()
            .then(() => updateStatus("Hazır", "registered"))
            .catch(() => updateStatus(UNAVAILABLE_MESSAGE, "error"));
        },
        onDisconnect: () => {
          ringTone.stop();
          updateStatus(UNAVAILABLE_MESSAGE, "error");
        },
      };

      await ua.start();
    } catch {
      updateStatus(UNAVAILABLE_MESSAGE, "error");
    }
  };
  }, [updateStatus, ringTone, clearConnectTimeout]);

  const makeCall = useCallback(async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const SIP = (window as any).SIP;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ua = userAgentRef.current as any;
    if (!SIP || !ua) return;

    if (endedTimerRef.current) {
      clearTimeout(endedTimerRef.current);
      endedTimerRef.current = null;
    }

    // Transport kopuksa arama denemesi anlamsız: INVITE gönderilemez, sunucudan
    // hiçbir yanıt gelmez ve arayüz "Aranıyor..."da asılı kalır. WebSocket
    // düştüğünde (onDisconnect) durum zaten "error" oluyor; buradan tekrar
    // denenirse kullanıcıya dürüst mesajı gösterip çık.
    const transportConnected =
      ua.isConnected?.() ?? ua.transport?.state === "Connected";
    if (!transportConnected) {
      ringTone.stop();
      updateStatus(UNAVAILABLE_MESSAGE, "error");
      return;
    }

    try {
      updateStatus("Aranıyor...", "calling");
      ringTone.start();

      const target = SIP.UserAgent.makeURI(sipConfigRef.current.target);
      const inviter = new SIP.Inviter(ua, target, {
        sessionDescriptionHandlerOptions: {
          constraints: { audio: true, video: false },
        },
      });

      // Referans ve dinleyici invite()'tan ÖNCE kurulmalı.
      //
      // Önceden ikisi de `await inviter.invite()` sonrasındaydı ve şu iki hatayı
      // üretiyordu (canlı destek log'unda birebir görüldü):
      //  1. invite() beklenirken oturum Establishing → Terminated'a geçebiliyor
      //     (transport hatası → dahili 503 → onReject). Dinleyici sonradan
      //     eklendiği için Terminated KAÇIRILIYOR, cleanup() hiç çalışmıyor ve
      //     arayüz "Temsilci aranıyor..." ekranında asılı kalıyordu.
      //  2. invite() reject ederse `sessionRef.current` hiç atanmıyordu; bu
      //     yüzden "Aramayı İptal Et" butonu hangup()'ın `if (!session) return`
      //     satırında sessizce çıkıyor, hiçbir şey yapmıyordu.
      sessionRef.current = inviter;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      inviter.stateChange.addListener((newState: any) => {
        if (newState === SIP.SessionState.Established) {
          ringTone.stop();
          updateStatus("Görüşme başladı", "active");
          attachRemoteAudio(inviter);
        }
        if (newState === SIP.SessionState.Terminated) {
          cleanup();
        }
      });

      await inviter.invite();

      if (inviter.sessionDescriptionHandler) {
        attachRemoteAudio(inviter);
      }
    } catch {
      // invite() reject etti — oturum Terminated'a geçmemiş olabilir, o yüzden
      // dinleyiciye güvenmeden arayüzü kendimiz toparlıyoruz.
      ringTone.stop();
      sessionRef.current = null;
      updateStatus("Arama başlatılamadı", "error");
    }
  }, [updateStatus, attachRemoteAudio, cleanup, ringTone]);

  /**
   * SIP oturumunu kapatır — arayüz durumuna DOKUNMAZ.
   *
   * Arayüz güncellemesinden ayrı tutuluyor çünkü iki çağıran farklı şey
   * istiyor: "Aramayı İptal Et" (hangup) "Arama sona erdi" ekranını görmek
   * ister, panel kapatma (close) ise doğrudan idle'a dönmek ister. Ayrıca
   * `bye()` transport kopukken asılabildiği için close bunu beklememeli.
   */
  const disposeSession = useCallback(async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const SIP = (window as any).SIP;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const session = sessionRef.current as any;
    sessionRef.current = null;
    if (!session) return;

    try {
      if (session.state === SIP.SessionState.Established) {
        await session.bye();
      } else if (session.state !== SIP.SessionState.Terminated) {
        // Terminated bir oturumda cancel/bye çağırmak sip.js'te hata fırlatır.
        await session.cancel?.();
      }
    } catch {
      // yut — dispose aşağıda her hâlükârda denenir
    }

    try {
      session.dispose?.();
    } catch {
      // zaten dispose edilmiş olabilir
    }
  }, []);

  const hangup = useCallback(async () => {
    ringTone.stop();
    // cleanup() stateChange dinleyicisine bırakılmıyor: oturum çoktan
    // Terminated ise yeni bir geçiş üretilmez, dinleyici hiç tetiklenmez ve
    // arayüz "Temsilci aranıyor..." ekranında kilitli kalırdı. Oturum hiç
    // kurulamamış olsa (invite reject) bile arayüz toparlanmalı.
    cleanup();
    await disposeSession();
  }, [cleanup, ringTone, disposeSession]);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    // Arayüz ÖNCE kapatılır. Eskiden `await hangup()` bekleniyordu; transport
    // kopukken bye/cancel yanıtsız kalabildiği için X butonu hiç tepki
    // vermiyormuş gibi görünüyordu. Kapanma artık SIP tarafına bağlı değil.
    if (endedTimerRef.current) {
      clearTimeout(endedTimerRef.current);
      endedTimerRef.current = null;
    }
    clearConnectTimeout();
    ringTone.stop();

    setIsOpen(false);
    setCallStatus("idle");
    setStatusMessage("Bağlanılıyor...");
    sipLoadedRef.current = false;
    initCalledRef.current = false;

    // Teardown best-effort ve beklenmiyor.
    const ua = userAgentRef.current;
    userAgentRef.current = null;
    void disposeSession().finally(() => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (ua as any)?.stop?.();
      } catch {
        // ignore
      }
    });
  }, [ringTone, clearConnectTimeout, disposeSession]);

  useEffect(() => {
    if (!isOpen || sipLoadedRef.current) return;

    const startSIP = () => {
      sipLoadedRef.current = true;
      void initSIPRef.current();
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).SIP) {
      startSIP();
      return;
    }

    const script = document.createElement("script");
    script.src = "/js/sip-0.21.2.min.js";
    script.async = true;
    script.onload = startSIP;
    script.onerror = () => updateStatus(UNAVAILABLE_MESSAGE, "error");
    document.body.appendChild(script);

    return () => {
      script.onload = null;
      script.onerror = null;
    };
  }, [isOpen, updateStatus]);

  useEffect(() => {
    return () => {
      if (endedTimerRef.current) clearTimeout(endedTimerRef.current);
      if (connectTimeoutRef.current) clearTimeout(connectTimeoutRef.current);
      ringTone.stop();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    isOpen,
    isConnected,
    callStatus,
    statusMessage,
    open,
    close,
    makeCall,
    hangup,
  };
};

export default useLiveSupport;
